import React, { useState, useEffect } from 'react';
import { IoMail, IoAttach, IoCalendar, IoDownload } from 'react-icons/io5';
import { FiMaximize2, FiMinimize2 } from 'react-icons/fi';

// Types et interfaces
interface EmailData {
  raw: string;
}

interface Attachment {
  filename: string;
  contentType: string;
  encoding?: string;
  size?: number;
  content?: string; 
}

interface ParsedEmail {
  from: string;
  to: string;
  cc?: string;
  date: string;
  subject: string;
  textBody: string;
  htmlBody: string;
  attachments: Attachment[];
  headers: Record<string, string>;
  raw: string;
}

interface MimeEmailViewerProps {
  emailData: EmailData | string | Record<string, any>;
  className?: string;
}

interface EmailProcessData {
  email?: {
    htmlContent?: string;
    textContent?: string;
    from?: string;
    to?: string;
    cc?: string;
    subject?: string;
    date?: string;
    attachments?: Attachment[];
    headers?: Record<string, string>;
  };
  process?: any;
  htmlContent?: string;
  textContent?: string;
  text?: string;
  from?: string;
  to?: string;
  cc?: string;
  subject?: string;
  date?: string;
  raw?: string;
  attachments?: Attachment[];
  headers?: Record<string, string>;
}

type ViewMode = 'html' | 'text';

interface MimePart {
  type: 'plain' | 'encoded';
  content: string;
  charset?: string;
  encoding?: string;
  fullMatch?: string;
}

interface ExtractedBodyParts {
  textBody: string;
  htmlBody: string;
  attachments: Attachment[];
}

const decodeMimeHeader = (encodedHeader: string): string => {
  if (!encodedHeader) return '';
  
  // Regex pour trouver les segments encodés MIME
  const mimeRegex = /=\?([^?]+)\?([BQ])\?([^?]*)\?=/gi;
  
  if (!encodedHeader.match(mimeRegex)) {
    return encodedHeader;
  }
  
  // Gestion des segments MIME concaténés (séparés par un espace) ex:  =?UTF-8?Q?Premier_segment?= =?UTF-8?Q?Deuxieme_segment?=
  let lastIndex = 0;
  const parts: MimePart[] = [];
  let currentMatch: RegExpExecArray | null;
  
  while ((currentMatch = mimeRegex.exec(encodedHeader)) !== null) {
    // Si c'est pas le début, et qu'il y a du texte entre les segments qui n'est pas juste un espace
    const textBetween = encodedHeader.substring(lastIndex, currentMatch.index).trim();
    if (lastIndex > 0 && textBetween && textBetween !== ' ') {
      parts.push({
        type: 'plain',
        content: encodedHeader.substring(lastIndex, currentMatch.index)
      });
    }
    
    parts.push({
      type: 'encoded',
      charset: currentMatch[1],
      encoding: currentMatch[2],
      content: currentMatch[3],
      fullMatch: currentMatch[0]
    });
    
    lastIndex = currentMatch.index + currentMatch[0].length;
  }
  
  if (lastIndex < encodedHeader.length) {
    parts.push({
      type: 'plain',
      content: encodedHeader.substring(lastIndex)
    });
  }
  
  let result = '';
  let lastPartWasEncoded = false;
  
  parts.forEach((part, index) => {
    if (part.type === 'plain') {
      // Si ce n'est pas juste un espace séparateur entre deux segments encodés
      if (!(lastPartWasEncoded && part.content.trim() === '' && 
           index < parts.length - 1 && parts[index + 1].type === 'encoded')) {
        result += part.content;
      }
      lastPartWasEncoded = false;
    } else {
      // Décodage selon le type d'encodage
      try {
        if (part.encoding?.toUpperCase() === 'Q') {
          result += decodeQuotedPrintableHeader(part.content);
        } else if (part.encoding?.toUpperCase() === 'B') {
          const decoded = atob(part.content);
          result += cleanupSpecialChars(decoded);
        } else {
          result += part.content;
        }
      } catch (e) {
        console.error('Erreur de décodage MIME:', e);
        result += part.content;
      }
      lastPartWasEncoded = true;
    }
  });
  
  return cleanupSpecialChars(result);
};

const cleanupSpecialChars = (text: string): string => {
  if (!text) return '';
  
  let result = text
    .replace(/dâ/g, "d'")
    .replace(/lâ/g, "l'")
    .replace(/nâ/g, "n'")
    .replace(/câ/g, "c'")
    .replace(/jâ/g, "j'")
    .replace(/mâ/g, "m'")
    .replace(/tâ/g, "t'")
    .replace(/sâ/g, "s'")
    .replace(/quâ/g, "qu'")

    .split("")
    .filter((char) => {
      const code = char.charCodeAt(0);
      return (
        (code >= 32 && code <= 126) || 
        code === 9 || // TAB
        code === 10 || // LF
        code === 13 || // CR
        code > 159
      ); 
    })
    .join("")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/[\u2000-\u200A]/g, " ")
    .replace(/\u00A0/g, " ")
    .replace(/\u202F/g, " ")
    .replace(/\u2007/g, " ")
    .replace(/\u2060/g, "")

    .replace(/['´`]/g, "'")
    .replace(/[\u2018\u2019\u201A\u201B\u2039\u203A\u00B4]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F\u00AB\u00BB]/g, '"')
    .replace(/Â°/g, "°")
    .replace(/Â/g, "")

    .replace(/([dljnmtsc])['´`]([a-z])/gi, "$1'$2")
    .replace(/([dljnmtsc])â([a-z])/gi, "$1'$2")
    .replace(/([dljnmtsc])Ã¢([a-z])/gi, "$1'$2")
    .replace(/([dljnmtsc])â€™([a-z])/gi, "$1'$2")
    .replace(/([cdeljnmtsu])=E2=80=99([a-z])/gi, "$1'$2")

    .replace(/=E2=80=99/g, "'")
    .replace(/=C3=A8/g, "è")
    .replace(/=C3=A9/g, "é")
    .replace(/=C3=AA/g, "ê")
    .replace(/=C3=AB/g, "ë")
    .replace(/=C3=A0/g, "à")
    .replace(/=C3=B9/g, "ù")
    .replace(/=C3=A7/g, "ç")
    .replace(/=C2=A0/g, " ")

    .replace(/Ã¨/g, "è")
    .replace(/Ã©/g, "é")
    .replace(/Ãª/g, "ê")
    .replace(/Ã«/g, "ë")
    .replace(/Ã /g, "à")
    .replace(/Ã¢/g, "â")
    .replace(/Ã¹/g, "ù")
    .replace(/Ã»/g, "û")
    .replace(/Ã®/g, "î")
    .replace(/Ã¯/g, "ï")
    .replace(/Ã´/g, "ô")
    .replace(/Ã¶/g, "ö")
    .replace(/Ã§/g, "ç")

    .replace(/Ã‰/g, "É")
    .replace(/Ãˆ/g, "È")
    .replace(/ÃŠ/g, "Ê")
    .replace(/Ã€/g, "À")
    .replace(/Ã‚/g, "Â")
    .replace(/Ã"/g, "Ô")
    .replace(/Ã‡/g, "Ç")

    .replace(/â‚¬/g, "€")
    .replace(/â€¦/g, "…")
    .replace(/â€"/g, "—")
    .replace(/â€"/g, "–")

    .replace(/nÂ°/g, "n°");
  
  result = result.replace(/\s+/g, ' ').trim();
  
  return result;
};

/**
 * Décode headers MIME
 */
const decodeQuotedPrintableHeader = (text: string): string => {
  if (!text) return '';
  
  let result = text
    .replace(/_/g, ' ')
    .replace(/=([0-9A-F]{2})/gi, (_, hex) => {
      const hexValue = parseInt(hex, 16);
      if (hexValue < 0 || hexValue > 255) {
        return `=${hex}`;
      }
      return String.fromCharCode(hexValue);
    });
  
  result = cleanupSpecialChars(result);
  
  return result;
};

/**
 * Décode body mails
 */
const decodeQuotedPrintable = (text: string): string => {
  if (!text) return '';
  
  // Prepare: fins de ligne + continuations
  let normalizedText = text
    // Supprimer les continuations de ligne (= à la fin d'une ligne)
    .replace(/=\r?\n/g, '')
    // Normaliser les fins de ligne
    .replace(/\r\n|\r|\n/g, '\n');
  
  // décodage des séquences hexadécimales (=XX)
  let decodedText = normalizedText.replace(/=([0-9A-F]{2})/gi, (_, hex) => {
    const hexValue = parseInt(hex, 16);
    if (hexValue < 0 || hexValue > 255) {
      return `=${hex}`; // Conserver la séquence si le décodage échoue
    }
    return String.fromCharCode(hexValue);
  });
  
  // décodage de l'UTF-8
  try {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder('utf-8');
    
    // If contient des caractères UTF-8 mal interprétés
    if (/[\u00C0-\u00DF][\u0080-\u00BF]|[\u00E0-\u00EF][\u0080-\u00BF]{2}|[\u00F0-\u00F7][\u0080-\u00BF]{3}/.test(decodedText)) {
      const encoded = encoder.encode(decodedText);
      decodedText = decoder.decode(encoded);
    }
  } catch (e) {
    console.error('Error during UTF-8 decoding:', e);
  }
  
  // Apostrophe curly typographique (')
  decodedText = decodedText.replace(/\u00e2\u0080\u0099/g, "'");
  
  const fixedText = cleanupSpecialChars(decodedText);
  
  return fixedText;
};


const downloadAttachment = (attachment: Attachment): void => {
  try {
    if (!attachment.content) {
      console.error('Pas de contenu disponible pour cette pièce jointe');
      alert('Impossible de télécharger cette pièce jointe : contenu manquant');
      return;
    }

    let binaryData: Uint8Array;
    
    if (attachment.encoding?.toLowerCase() === 'base64') {
      const binaryString = atob(attachment.content.replace(/\s+/g, ''));
      binaryData = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        binaryData[i] = binaryString.charCodeAt(i);
      }
    } else {
      const encoder = new TextEncoder();
      binaryData = encoder.encode(attachment.content);
    }

    const blob = new Blob([binaryData], { 
      type: attachment.contentType || 'application/octet-stream' 
    });

    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = attachment.filename;
    a.style.display = 'none';
    
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    
    URL.revokeObjectURL(url);
    
    console.log(`Téléchargement initié pour: ${attachment.filename}`);
  } catch (error) {
    console.error('Erreur lors du téléchargement:', error);
    alert(`Erreur lors du téléchargement de ${attachment.filename}`);
  }
};


const MimeEmailViewer = ({ emailData, className = '' }: MimeEmailViewerProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<ViewMode>('html');
  const [parsedEmail, setParsedEmail] = useState<ParsedEmail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (emailData) {
      setLoading(true);
      const parsed = parseEmailContent(emailData);
      console.log(parsed);
      setParsedEmail(parsed);
      setLoading(false);
    }
  }, [emailData]);

  const toggleViewMode = (mode: ViewMode): void => {
    setViewMode(mode);
  };

  const toggleExpand = (): void => {
    setIsExpanded(!isExpanded);
  };

  const viewerClasses = isExpanded
    ? 'fixed inset-0 z-50 bg-white overflow-auto'
    : `rounded-lg shadow-sm bg-white overflow-hidden ${className}`;

  if (loading ?? !parsedEmail) {
    return (
      <div className="flex justify-center items-center p-6 bg-white border rounded-lg">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-gray-200 h-10 w-10"></div>
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderEmailBody = () => {
    switch (viewMode) {
      case 'html':
        return parsedEmail.htmlBody ? (
          <div
            className="email-body-html p-4 min-h-[300px] overflow-auto"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(parsedEmail.htmlBody) }}
          />
        ) : (
          <div className="p-4 text-gray-600 italic">
            Pas de contenu HTML disponible.{" "}
            <button className="text-vistagreen hover:underline" type="button" onClick={() => setViewMode("text")}>
              Voir en texte brut
            </button>
          </div>
        );
      
      case 'text': {
        const cleanedTextBody = parsedEmail.textBody ? 
          parsedEmail.textBody.replace(/^.*?(?:pièce\s*jointe|attachement|attachment).*$/gmi, '') : '';
          
        return (
          <div className="email-body-text p-4 min-h-[300px] max-h-[600px] overflow-auto whitespace-pre-wrap font-mono text-sm text-gray-800">
            {cleanedTextBody ?? 'Pas de contenu texte disponible.'}
          </div>
        );
      }
      
      default:
        return <div />;
    }
  };

  const renderAttachments = () => {
    if (!parsedEmail.attachments || parsedEmail.attachments.length === 0) {
      return null;
    }
  
    return (
      <div className="border-t border-gray-200 p-3">
        <h4 className="text-xs uppercase text-gray-500 mb-2 font-semibold">
          Pièces jointes ({parsedEmail.attachments.length})
        </h4>
        <div className="flex flex-wrap gap-2">
          {parsedEmail.attachments.map((attachment, index) => {
            const fileIcon = <IoAttach className="mr-1.5" />;
            const fileExt = attachment.filename?.split('.').pop()?.toLowerCase();
            let fileClass = "bg-gray-100";
            
            if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'jpg', 'jpeg', 'png'].includes(fileExt ?? '')) {
              fileClass = "bg-blue-50";
            }
            
            const uniqueKey = `${attachment.filename}-${attachment.contentType}-${index}`;
            const hasContent = !!attachment.content;

            return (
              <button
                key={uniqueKey}
                className={`flex items-center rounded-md ${fileClass} px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer ${!hasContent ? 'opacity-50' : ''}`}
                title={hasContent ? `Télécharger ${attachment.filename} (${attachment.contentType})` : `${attachment.filename} - Contenu non disponible`}
                onClick={() => hasContent ? downloadAttachment(attachment) : handleAttachmentClick(attachment)}
                disabled={!hasContent}
                type="button"
              >
                {fileIcon}
                <span className="truncate max-w-[150px]">{attachment.filename}</span>
                <IoDownload className={`ml-1.5 ${hasContent ? 'text-gray-500' : 'text-gray-300'}`} />
              </button>
            );
          })}
        </div>
      </div>
    );
  };
  
  const handleAttachmentClick = (attachment: Attachment): void => {
    console.log("Pièce jointe cliquée:", attachment);
    
    const event = new CustomEvent('attachment-click', { 
      detail: { attachment } 
    });
    document.dispatchEvent(event);
  };

  return (
    <div className={viewerClasses}>
      <div className="flex justify-between bg-gray-50 p-3 border-b border-gray-200">
        <div className="flex items-center">
          <IoMail className="text-vistagreen mr-2" />
          <h3 className="text-sm font-medium">Email Original</h3>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex bg-gray-200 rounded-md overflow-hidden">
            <button
              onClick={() => toggleViewMode('html')}
              type="button"
              className={`text-xs px-2 py-1 transition-colors ${viewMode === 'html' ? 'bg-vistagreen text-white' : 'hover:bg-gray-300'}`}
            >
              HTML
            </button>
            <button
              onClick={() => toggleViewMode('text')}
              type="button"
              className={`text-xs px-2 py-1 transition-colors ${viewMode === 'text' ? 'bg-vistagreen text-white' : 'hover:bg-gray-300'}`}
            >
              Texte
            </button>
          </div>
          <button
            onClick={toggleExpand}
            type="button"
            className="text-gray-500 hover:text-gray-700 p-1"
            title={isExpanded ? 'Réduire' : 'Agrandir'}
          >
            {isExpanded ? <FiMinimize2 /> : <FiMaximize2 />}
          </button>
        </div>
      </div>

      <div className="bg-white p-3 border-b border-gray-200">
        <div className="grid gap-1 text-sm">
          <div className="grid grid-cols-[80px_1fr] items-baseline">
            <span className="text-gray-500 font-medium">De:</span>
            <span className="text-gray-800 truncate">{parsedEmail.from ?? 'Inconnu'}</span>
          </div>
          <div className="grid grid-cols-[80px_1fr] items-baseline">
            <span className="text-gray-500 font-medium">À:</span>
            <span className="text-gray-800 truncate">{parsedEmail.to ?? 'Inconnu'}</span>
          </div>
          {parsedEmail.cc && (
            <div className="grid grid-cols-[80px_1fr] items-baseline">
              <span className="text-gray-500 font-medium">Cc:</span>
              <span className="text-gray-800 truncate">{parsedEmail.cc}</span>
            </div>
          )}
          <div className="grid grid-cols-[80px_1fr] items-baseline">
            <span className="text-gray-500 font-medium">Date:</span>
            <div className="flex items-center text-gray-800">
              <IoCalendar className="mr-1 text-gray-400" />
              <span>{parsedEmail.date ?? 'Inconnue'}</span>
            </div>
          </div>
          <div className="grid grid-cols-[80px_1fr] items-baseline">
            <span className="text-gray-500 font-medium">Objet:</span>
            <span className="text-gray-900 font-medium">{parsedEmail.subject ?? '(Pas d\'objet)'}</span>
          </div>
        </div>
      </div>

      <div className="relative bg-white border-b border-gray-200">
        {renderEmailBody()}
      </div>

      {renderAttachments()}
    </div>
  );
};

const parseEmailContent = (data: EmailData | string | Record<string, any>): ParsedEmail => {
  if (typeof data === 'object' && data !== null) {
    const processData = (data as EmailProcessData).process ?? data as EmailProcessData;
    const emailData = processData.email ?? {};
    
    let htmlContent = emailData.htmlContent ?? processData.htmlContent;
    
    let textContent = emailData.textContent ?? processData.textContent;
    if (processData.text && typeof processData.text === 'string') {
      textContent = processData.text;
    }
    
    if ((!htmlContent || !textContent) && processData.raw) {
      const extractedContent = extractContentFromRaw(processData.raw);
      htmlContent = htmlContent ?? extractedContent.htmlBody;
      textContent = textContent ?? extractedContent.textBody;
    }

    const from = decodeMimeHeader(emailData.from ?? processData.from ?? '');
    const to = decodeMimeHeader(emailData.to ?? processData.to ?? '');
    const cc = decodeMimeHeader(emailData.cc ?? processData.cc ?? '');
    const subject = decodeMimeHeader(emailData.subject ?? processData.subject ?? '');

    const cleanedHtmlContent = htmlContent ? cleanupSpecialChars(htmlContent) : '';
    const cleanedTextContent = textContent ? cleanupSpecialChars(textContent) : '';

    return {
      from,
      to,
      cc,
      date: formatDate(emailData.date ?? processData.date),
      subject,
      textBody: cleanedTextContent,
      htmlBody: cleanedHtmlContent,
      attachments: emailData.attachments ?? processData.attachments ?? [],
      headers: emailData.headers ?? processData.headers ?? {},
      raw: processData.raw ?? JSON.stringify(data, null, 2)
    };
  }
  
  if (typeof data === 'string') {
    return extractContentFromRaw(data);
  }
  
  return {
    from: '',
    to: '',
    cc: '',
    date: '',
    subject: '',
    textBody: '',
    htmlBody: '',
    attachments: [],
    headers: {},
    raw: typeof data === 'string' ? data : JSON.stringify(data, null, 2)
  };
};

const extractContentFromRaw = (rawEmail: string): ParsedEmail => {
  const headers = extractAllHeaders(rawEmail);
  
  const extractedHeaders = {
    from: decodeMimeHeader(headers['From'] ?? ''),
    to: decodeMimeHeader(headers['To'] ?? ''),
    cc: decodeMimeHeader(headers['Cc'] ?? ''),
    date: formatDate(headers['Date'] ?? ''),
    subject: decodeMimeHeader(headers['Subject'] ?? ''),
  };

  const { textBody, htmlBody, attachments } = extractBodyParts(rawEmail);

  const cleanedHtmlBody = htmlBody ? cleanupSpecialChars(htmlBody) : '';
  const cleanedTextBody = textBody ? cleanupSpecialChars(textBody) : '';

  return {
    ...extractedHeaders,
    textBody: cleanedTextBody,
    htmlBody: cleanedHtmlBody,
    attachments,
    headers,
    raw: rawEmail
  };
};

const extractAllHeaders = (rawEmail: string): Record<string, string> => {
  const headers: Record<string, string> = {};
  const headerSection = rawEmail.split(/\r?\n\r?\n/)[0] ?? '';
  
  let currentHeader = '';
  headerSection.split(/\r?\n/).forEach(line => {
    if (/^\s+/.test(line) && currentHeader) {
      headers[currentHeader] = (headers[currentHeader] + ' ' + line.trim()).trim();
    } else {
      const regex = /^([^:]{1,100}):\s*(.{0,1000})$/;
      const match = regex.exec(line);
      if (match) {
        currentHeader = match[1].trim();
        headers[currentHeader] = match[2].trim();
      }
    }
  });
  
  return headers;
};

const extractBodyParts = (rawEmail: string): ExtractedBodyParts => {
  let textBody = '';
  let htmlBody = '';
  const attachments: Attachment[] = [];
  
  console.log("🚀 Starting MIME parsing with fixed recursive extractor...");

  // Fonction récursive pour extraire des boundaries d'une chaîne Content-Type
  function extractBoundaryFromContentType(contentType: string): string | null {
    const boundaryRegex = /boundary=["']?([^"';\s]+)["']?/i;
    const boundaryMatch = boundaryRegex.exec(contentType);
    return boundaryMatch ? boundaryMatch[1] : null;
  }

function extractHeaders(headerSection: string): Record<string, string> {
  const headers: Record<string, string> = {};
  
  headerSection.split(/\r?\n/).forEach(line => {
    if (!line.trim()) return;
    
    // Gérer les lignes de continuation
    if (/^\s+/.test(line) && Object.keys(headers).length > 0) {
      const lastHeader = Object.keys(headers).pop();
      if (lastHeader) {
        headers[lastHeader] += ' ' + line.trim();
      }
      return;
    }
    
    // Extraire nom et valeur de l'en-tête
    const regex = /^([^:]{1,100}):\s*(.{0,1000})$/;
    const match = regex.exec(line);
    if (match) {
      const name = match[1].trim();
      const value = match[2].trim();
      headers[name] = value;
    }
  });
  
  return headers;
}

function decodeContent(body: string, encoding: string): string {
  const lowerEncoding = encoding.toLowerCase();
  
  if (lowerEncoding === 'quoted-printable') {
    return decodeQuotedPrintable(body);
  }
  
  if (lowerEncoding === 'base64') {
    try {
      return atob(body.replace(/\s+/g, ''));
    } catch (e) {
      console.error('❌ Error decoding base64:', e);
      return body;
    }
  }
  
  return body;
}

function handleMultipartContent(body: string, contentType: string, depth: number): void {
  const indent = ' '.repeat(depth * 2);
  const boundary = extractBoundaryFromContentType(contentType);
  
  if (!boundary) {
    console.log(`${indent}⚠️ Multipart content without boundary!`);
    return;
  }
  
  console.log(`${indent}🔍 Found boundary: "${boundary}"`);
  
  const boundaryRegex = new RegExp(`--${boundary}(?:--)?[\r\n]+`, 'g');
  const subParts = body.split(boundaryRegex);
  
  console.log(`${indent}🔢 Found ${subParts.length} subparts`);
  
  subParts.forEach((subPart, index) => {
    if (!subPart.trim()) return;
    console.log(`${indent}🔄 Processing subpart ${index + 1} of ${subParts.length}`);
    parsePart(subPart, depth + 1);
  });
}

function handleTextContent(body: string, contentType: string, encoding: string, depth: number): void {
  const indent = ' '.repeat(depth * 2);
  const decodedContent = decodeContent(body, encoding);
  const cleanedContent = cleanupSpecialChars(decodedContent);
  
  if (contentType.toLowerCase().includes('text/plain')) {
    console.log(`${indent}📝 Found text/plain content`);
    textBody = cleanedContent;
    console.log(`${indent}✅ Extracted ${textBody.length} chars of text`);
  } else if (contentType.toLowerCase().includes('text/html')) {
    console.log(`${indent}🌐 Found text/html content`);
    htmlBody = cleanedContent;
    console.log(`${indent}✅ Extracted ${htmlBody.length} chars of HTML`);
  }
}

function handleAttachment(headers: Record<string, string>, contentType: string, body: string, depth: number): void {
  const indent = ' '.repeat(depth * 2);
  const filenameRegex = /filename=["']?([^"';\r\n]+)["']?/i;
  const contentDisposition = headers['Content-Disposition'];
  const match = filenameRegex.exec(contentDisposition ?? '');
  const filename = match?.[1] ?? 'attachment';
  const encoding = headers['Content-Transfer-Encoding'] ?? '';
  
  console.log(`${indent}📎 Found attachment: ${filename} (encoding: ${encoding})`);
  
  const cleanedContent = encoding.toLowerCase() === 'base64' 
    ? body.replace(/\r?\n/g, '').trim()
    : body;
  
  attachments.push({
    filename,
    contentType,
    encoding,
    content: cleanedContent
  });
}

function parsePart(part: string, depth = 0): void {
  const indent = ' '.repeat(depth * 2);
  console.log(`${indent}📦 Parsing part (depth: ${depth})`);
  
  const headerBodySplit = part.split(/\r?\n\r?\n/);
  if (headerBodySplit.length < 2) {
    console.log(`${indent}⚠️ Part has no headers/body separation`);
    return;
  }
  
  const headers = extractHeaders(headerBodySplit[0]);
  const body = headerBodySplit.slice(1).join('\r\n\r\n');
  
  const contentType = headers['Content-Type'] ?? '';
  const contentEncoding = headers['Content-Transfer-Encoding'] ?? '';
  
  console.log(`${indent}📄 Content-Type: "${contentType}"`);
  
  const lowerContentType = contentType.toLowerCase();
  
  if (lowerContentType.includes('multipart/')) {
    handleMultipartContent(body, contentType, depth);
  } else if (lowerContentType.includes('text/')) {
    handleTextContent(body, contentType, contentEncoding, depth);
  } else if (headers['Content-Disposition']?.toLowerCase().includes('attachment')) {
    handleAttachment(headers, contentType, body, depth);
  } else {
    console.log(`${indent}ℹ️ Unhandled content type: ${contentType}`);
  }
}
  
  parsePart(rawEmail);
  
  console.log("🏁 Extraction completed:");
  console.log(`- Text body: ${textBody.length} chars`);
  console.log(`- HTML body: ${htmlBody.length} chars`);
  console.log(`- Attachments: ${attachments.length}`);
  
  return { textBody, htmlBody, attachments };
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr; 
  
  return date.toLocaleString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const sanitizeHtml = (html: string): string => {
  if (!html) return '';
  
  // Supprimer les éléments de script et événements pour la sécurité
  let cleanedHtml = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '')
    .replace(/on\w+=\w+/gi, '')
    // Supprimer javascript: mais préserver mailto:, tel:, etc.
    .replace(/href\s*=\s*["']javascript:/gi, 'href="removed:');
  
  // Supprimer les liens de téléchargement typiques des pièces jointes (limite la longueur pour éviter ReDoS)
  cleanedHtml = cleanedHtml.replace(/<a\s+[^>]{0,500}(?:attachment|download|pièce\s*jointe)[^>]{0,200}>[\s\S]{0,1000}<\/a>/gi, '');
  
  // Supprimer les sections dédiées aux pièces jointes que certains clients mail ajoutent
  cleanedHtml = cleanedHtml.replace(/<div\s+[^>]{0,500}(?:attachment|piece-jointe|fichier-joint)[^>]{0,200}>[\s\S]{0,2000}<\/div>/gi, '');
  
  // Supprimer les objets embarqués qui peuvent être des pièces jointes (limite la longueur)
  cleanedHtml = cleanedHtml.replace(/<object\s+[^>]{0,500}>[\s\S]{0,2000}<\/object>/gi, '');
  
  // Supprimer les images embarquées qui sont des pièces jointes (cid:) - limite la longueur
  cleanedHtml = cleanedHtml.replace(/<img\s+[^>]{0,300}src=["'](?:cid:|attachment:)[^"']{0,200}["'][^>]{0,200}>/gi, '');
  
  // Retirer les sections spécifiques que les clients mail comme Outlook ajoutent pour les pièces jointes
  cleanedHtml = cleanedHtml.replace(/<div\s+[^>]{0,500}(?:attachments|attachment-list|attached-files)[^>]{0,200}>[\s\S]{0,3000}<\/div>/gi, '');
  
  // Add target="_blank"
  cleanedHtml = addTargetBlankToLinks(cleanedHtml);
  
  return cleanedHtml;
};

const addTargetBlankToLinks = (html: string): string => {
  let result = '';
  let currentPos = 0;
  
  const linkRegex = /<a\s+/gi;
  let match;
  
  while ((match = linkRegex.exec(html)) !== null) {
    result += html.slice(currentPos, match.index);
    
    let tagStart = match.index;
    let tagEnd = findTagEnd(html, tagStart);
    
    if (tagEnd === -1) {
      result += html.slice(tagStart);
      break;
    }
    
    let tagContent = html.slice(tagStart, tagEnd + 1);
    
    if (isHttpLink(tagContent) && !hasTargetAttribute(tagContent)) {
      tagContent = tagContent.slice(0, -1) + ' target="_blank" rel="noopener noreferrer">';
    }
    
    result += tagContent;
    currentPos = tagEnd + 1;
  }
  
  result += html.slice(currentPos);
  
  return result;
};

const findTagEnd = (html: string, startPos: number): number => {
  let pos = startPos + 1;
  let inQuotes = false;
  let quoteChar = '';
  
  while (pos < html.length) {
    const char = html[pos];
    
    if (!inQuotes && (char === '"' || char === "'")) {
      inQuotes = true;
      quoteChar = char;
    } else if (inQuotes && char === quoteChar) {
      if (pos === 0 || html[pos - 1] !== '\\') {
        inQuotes = false;
        quoteChar = '';
      }
    } else if (!inQuotes && char === '>') {
      return pos;
    }
    
    pos++;
  }
  
  return -1;
};

const isHttpLink = (tagContent: string): boolean => {
  const httpRegex = /href\s*=\s*["']https?:/i;
  return httpRegex.test(tagContent);
};

const hasTargetAttribute = (tagContent: string): boolean => {
  const targetRegex = /target\s*=\s*["']/i;
  return targetRegex.test(tagContent);
};

export default MimeEmailViewer;